import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CommunityHeader, BadgeTierView, AuthorityChainView } from '@/components/badges';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  DEMO_COMMUNITY, 
  DEMO_BADGES, 
  DEMO_AWARDS, 
  DEMO_PUBKEYS,
  getDemoProfile,
  getDemoBadge,
  RANK_NAMES 
} from '@/lib/demoData';
import type { BadgeReference, BadgeTier, AuthorityChain } from '@/types/badges';
import { buildBadgeTiers, validateAuthorityChain, getChainDepth } from '@/lib/badgeUtils';

// Build demo badge tiers
function buildDemoBadgeTiers(): BadgeTier[] {
  const community = DEMO_COMMUNITY;
  const badges = DEMO_BADGES.map(b => ({
    kind: 30009,
    pubkey: b.pubkey,
    identifier: b.identifier,
    relay: undefined,
    rank: b.rank,
  }));

  const communityMeta = {
    id: community.id,
    name: community.name,
    badges,
    founders: [DEMO_PUBKEYS.alice, DEMO_PUBKEYS.bob],
    relays: ['wss://relay.example.com'],
  };

  // Convert awards to BadgeAward format
  const awards = DEMO_AWARDS.map(a => ({
    kind: 8 as const,
    pubkey: a.granter,
    tags: [
      ['a', `30009:${DEMO_PUBKEYS.alice}:${a.badgeIdentifier}`],
      ['p', a.recipient],
    ],
    content: '',
    id: `demo-${a.granter}-${a.recipient}`,
    created_at: a.timestamp,
    sig: 'demo',
  }));

  return buildBadgeTiers(communityMeta, awards, new Set());
}

// Validate a single award
function validateDemoAward(
  granter: string, 
  recipient: string, 
  badgeId: string
): AuthorityChain | null {
  const badges = DEMO_BADGES.map(b => ({
    kind: 30009,
    pubkey: b.pubkey,
    identifier: b.identifier,
    relay: undefined,
    rank: b.rank,
  }));

  const communityMeta = {
    id: DEMO_COMMUNITY.id,
    name: DEMO_COMMUNITY.name,
    badges,
    founders: [DEMO_PUBKEYS.alice, DEMO_PUBKEYS.bob],
    relays: ['wss://relay.example.com'],
  };

  const awards = DEMO_AWARDS.map(a => ({
    kind: 8 as const,
    pubkey: a.granter,
    tags: [
      ['a', `30009:${DEMO_PUBKEYS.alice}:${a.badgeIdentifier}`],
      ['p', a.recipient],
    ],
    content: '',
    id: `demo-${a.granter}-${a.recipient}`,
    created_at: a.timestamp,
    sig: 'demo',
  }));

  const award = awards.find(
    a => a.pubkey === granter && a.tags.find(t => t[0] === 'p')?.[1] === recipient
  );

  if (!award) return null;
  return validateAuthorityChain(award, communityMeta, awards, new Set());
}

export default function CommunityPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  
  // For demo, always use demo community
  const community = DEMO_COMMUNITY;
  const badgeTiers = buildDemoBadgeTiers();

  // Group tiers by rank
  const tiersByRank = new Map<number, BadgeTier[]>();
  for (const tier of badgeTiers) {
    const existing = tiersByRank.get(tier.rank) || [];
    existing.push(tier);
    tiersByRank.set(tier.rank, existing);
  }

  // Get chain for display
  const selectedChain = selectedTier !== null 
    ? validateDemoAward(DEMO_PUBKEYS.eve, DEMO_PUBKEYS.iris, 'member')
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <CommunityHeader
        communityId={community.id}
        originPubkey={community.originPubkey}
        memberCount={8}
      />

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue="hierarchy" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="hierarchy">Hierarchy</TabsTrigger>
            <TabsTrigger value="badges">Badge Tiers</TabsTrigger>
            <TabsTrigger value="chains">Authority Chains</TabsTrigger>
          </TabsList>

          {/* Hierarchy Tab */}
          <TabsContent value="hierarchy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HierarchyIcon className="w-5 h-5" />
                  Authority Hierarchy Tree
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {/* Rank 0: Founders */}
                  <div>
                    <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center text-xs font-bold">
                        0
                      </span>
                      {RANK_NAMES[0]}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {[DEMO_PUBKEYS.alice, DEMO_PUBKEYS.bob].map(pubkey => {
                        const profile = getDemoProfile(pubkey);
                        return (
                          <div key={pubkey} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                            <img
                              src={profile.picture}
                              alt={profile.name}
                              className="w-12 h-12 rounded-full border-2 border-amber-400"
                            />
                            <div>
                              <p className="font-semibold text-amber-900 dark:text-amber-100">
                                {profile.name}
                              </p>
                              <p className="text-xs text-amber-600 dark:text-amber-400">
                                Can award any badge
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Rank 1: Core Maintainers */}
                  <div>
                    <h3 className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-xs font-bold">
                        1
                      </span>
                      {RANK_NAMES[1]}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {[DEMO_PUBKEYS.carol, DEMO_PUBKEYS.dave].map(pubkey => {
                        const profile = getDemoProfile(pubkey);
                        const awardedBy = DEMO_AWARDS.find(a => a.recipient === pubkey)?.granter;
                        const granterProfile = awardedBy ? getDemoProfile(awardedBy) : null;
                        
                        return (
                          <div key={pubkey} className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                            <img
                              src={profile.picture}
                              alt={profile.name}
                              className="w-12 h-12 rounded-full border-2 border-purple-400"
                            />
                            <div>
                              <p className="font-semibold text-purple-900 dark:text-purple-100">
                                {profile.name}
                              </p>
                              <p className="text-xs text-purple-600 dark:text-purple-400">
                                Can award Contributor & Member badges
                              </p>
                              {granterProfile && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Awarded by {granterProfile.name}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Rank 2: Contributors */}
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-xs font-bold">
                        2
                      </span>
                      {RANK_NAMES[2]}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* East Contributors */}
                      <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">
                          East Coast Chapter
                        </p>
                        <div className="space-y-2">
                          {[DEMO_PUBKEYS.eve, DEMO_PUBKEYS.frank].map(pubkey => {
                            const profile = getDemoProfile(pubkey);
                            return (
                              <div key={pubkey} className="flex items-center gap-2">
                                <img
                                  src={profile.picture}
                                  alt={profile.name}
                                  className="w-8 h-8 rounded-full border border-emerald-300"
                                />
                                <span className="text-sm">{profile.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* West Contributors */}
                      <div className="p-4 rounded-lg bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800">
                        <p className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-2">
                          West Coast Chapter
                        </p>
                        <div className="space-y-2">
                          {[DEMO_PUBKEYS.grace, DEMO_PUBKEYS.henry].map(pubkey => {
                            const profile = getDemoProfile(pubkey);
                            return (
                              <div key={pubkey} className="flex items-center gap-2">
                                <img
                                  src={profile.picture}
                                  alt={profile.name}
                                  className="w-8 h-8 rounded-full border border-teal-300"
                                />
                                <span className="text-sm">{profile.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Each contributor can award Member badges within their chapter.
                    </p>
                  </div>

                  {/* Rank 3: Members */}
                  <div>
                    <h3 className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-bold">
                        3
                      </span>
                      {RANK_NAMES[3]}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {[DEMO_PUBKEYS.iris, DEMO_PUBKEYS.jack].map(pubkey => {
                        const profile = getDemoProfile(pubkey);
                        const awardedBy = DEMO_AWARDS.find(a => a.recipient === pubkey)?.granter;
                        const granterProfile = awardedBy ? getDemoProfile(awardedBy) : null;
                        
                        return (
                          <div key={pubkey} className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                            <img
                              src={profile.picture}
                              alt={profile.name}
                              className="w-10 h-10 rounded-full border-2 border-blue-400"
                            />
                            <div>
                              <p className="font-semibold text-blue-900 dark:text-blue-100">
                                {profile.name}
                              </p>
                              <p className="text-xs text-blue-600 dark:text-blue-400">
                                Awarded by {granterProfile?.name}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {DEMO_BADGES.map(badge => {
                const holders = DEMO_AWARDS.filter(a => a.badgeIdentifier === badge.identifier);
                
                return (
                  <BadgeTierView
                    key={badge.identifier}
                    rank={badge.rank}
                    badgeName={badge.name}
                    badgeImage={badge.thumb || badge.image}
                    holders={holders.map(a => ({
                      pubkey: a.recipient,
                      isValid: true,
                    }))}
                  />
                );
              })}
            </div>
          </TabsContent>

          {/* Chains Tab */}
          <TabsContent value="chains" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ChainIcon className="w-5 h-5" />
                  Authority Chain Examples
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Example 1: Alice -> Carol -> Eve -> Iris */}
                  <AuthorityChainView
                    chain={validateDemoAward(DEMO_PUBKEYS.eve, DEMO_PUBKEYS.iris, 'member')!}
                    className="border-l-4 border-l-amber-400"
                  />
                  
                  {/* Example 2: Alice -> Dave -> Grace */}
                  <AuthorityChainView
                    chain={validateDemoAward(DEMO_PUBKEYS.dave, DEMO_PUBKEYS.grace, 'west-contributor')!}
                    className="border-l-4 border-l-purple-400"
                  />
                  
                  {/* Example 3: Alice -> Carol -> Frank -> Jack */}
                  <AuthorityChainView
                    chain={validateDemoAward(DEMO_PUBKEYS.frank, DEMO_PUBKEYS.jack, 'member')!}
                    className="border-l-4 border-l-emerald-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How Chain Validation Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      <strong>Founder Authority:</strong> Founders (rank 0) can award any badge in the hierarchy
                    </li>
                    <li>
                      <strong>Rank-Based Delegation:</strong> A badge holder at rank N can only award badges at rank M where M &gt; N
                    </li>
                    <li>
                      <strong>Chain Validation:</strong> Clients trace each award back to a founder, verifying each link in the chain
                    </li>
                    <li>
                      <strong>Revocation Propagation:</strong> When a badge is revoked, all downstream badges become invalid
                    </li>
                  </ol>
                  
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Cross-Chapter Granting
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      A badge holder at rank N can award badges at rank M where M &gt; N, <em>regardless of chapter</em>. 
                      An East Contributor (rank 2) can award Member badges to West Coast members.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <footer className="border-t bg-card py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>
            Built with{" "}
            <a 
              href="https://shakespeare.diy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Shakespeare
            </a>
            {" "}• Hierarchical Badges Demo (NIP-XX Draft)
          </p>
        </div>
      </footer>
    </div>
  );
}

function HierarchyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="15" y="3" width="6" height="6" rx="1" />
      <rect x="9" y="15" width="6" height="6" rx="1" />
      <path d="M6 9v3a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V9" />
      <path d="M12 12v3" />
    </svg>
  );
}

function ChainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}
